import { gql } from "@apollo/client";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { ComponentStateTypes } from "components/common/ComponentState";
import HTMLCode from "components/common/HTMLCode";
import ItemView, { ItemViewPropsType } from "components/common/views/ItemView";
import { graph } from "config/axios-context";
import { AppRoutes, resolveRoute } from "config/router";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AgricultureWorkInvestmentViewQuery, AgricultureWorkInvestmentViewQueryVariables } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";
import Page, { PageActionsType } from "../../../../components/common/page/Page";

type ModelType = NonNullable<AgricultureWorkInvestmentViewQuery["agricultureWorkInvestment"]>;

const agricultureInvestmentQuery = gql`
query AgricultureWorkInvestmentView($id: Int!) {
    agricultureWorkInvestment(id: $id) {
        id
        name,
        startDate,
        season {
          name
          id,
          active
        }
        unit
        unitPrice
        total
        quantity
        investmentType
        category
        type
        note
        workers {
          items {
            person {
              name
              id
            }
          }
        }
    }
  }
`;

const AgricultureInvestmentViewPage = () => {
    const params = useParams<{id: string}>();
    const context = useAppContext();
    const [model, setModel] = useState<ModelType>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        const id = Number(params.id);
        if (!id) {
            setComponentState('nodata');
            return;
        }
        const result = await graph<AgricultureWorkInvestmentViewQuery, AgricultureWorkInvestmentViewQueryVariables>({
            query: agricultureInvestmentQuery,
            variables: {
                id
            }
        });

        if(result.errors?.length) {
            context.notify('error', 'error.loading-data');
            throw result.errors;
        }

        if (!result.data?.agricultureWorkInvestment) {
            setComponentState('nodata');
        } else {
            setModel(result.data.agricultureWorkInvestment!);
            setComponentState('');
        }
    };

    const renderView = () : ItemViewPropsType['model'] => {
        if(!model) return [];
        return [
            {
                name: 'id',
                items: [
                    {
                        label: 'fields.id.name',
                        value: model.id,
                        name: 'id',
                        propertyType: 'number'
                    },
                    { 
                        label: 'fields.name.name',
                        value: model.name,
                        name: 'name',
                        propertyType: 'string'
                    },
                    {
                        label: 'fields.date.name',
                        value: model.startDate,
                        name: 'startDate',
                        propertyType: 'datetime'
                    },
                    {
                        label: '',
                        name: 'fist-separator',
                        value: <hr/>
                    },
                    {
                        label: 'fields.quantity.name',
                        value: <span>{model.quantity}<HTMLCode />{model.unit}</span>,
                        name: 'unit',
                    },
                    {
                        label: 'fields.unitPrice.name',
                        value: <span>{model.unitPrice} <HTMLCode code="euro" /> / {model.unit}</span>,
                        name: 'unitPrice',
                    },
                    {
                        label: 'fields.total.name',
                        value: model.total,
                        name: 'total',
                        propertyType: 'money'
                    },
                    {
                        label: '',
                        name: 'second-separator',
                        value: <hr/>
                    },
                    {
                        label: 'fields.person.name',
                        value: model.workers?.items?.map(x => x.person.name).join(', '),
                        name: 'seller'
                    },
                    {
                        label: 'fields.investmentType.name',
                        value: model.investmentType,
                        name: 'investmentType',
                    },
                    {
                        label: 'fields.category.name',
                        value: model.category,
                        name: 'category',
                    },
                    {
                        label: 'fields.type.name',
                        value: model.type,
                        name: 'type',
                    },
                    {
                        label: 'fields.season.name',
                        value: model.season.name,
                        name: 'season',
                    },
                    {
                        label: 'fields.note.name',
                        value: model.note,
                        name: 'note',
                        propertyType: 'note'
                    }
                ]
            }
        ];
    }

    const actions: PageActionsType = [
        {
            icon: faPen,
            link: resolveRoute(AppRoutes.AGRICULTURE_INVESTMENT_EDIT, { id: params.id }),
            title: context.t('button.edit'),
            name: "edit"
        }
    ];

    return (
            <Page title="pages.agriculture.work.investment.view" state={componentState} actions={actions}>
                <ItemView model={renderView()} name="investment-view-page" view="default" />
            </Page>
    );
}

export default AgricultureInvestmentViewPage;
